/**
 * Minimal pre-send tweak so bulleted/numbered lists actually render in
 * email clients. Outlook and many others strip the user-agent default
 * styles that browsers apply to <ul>/<ol>/<li>, so without explicit
 * inline styles the markers disappear and items render flush-left.
 *
 * Everything else — fonts, colors, spacing, line breaks — is passed
 * through exactly as the user typed it. No wrappers added.
 */

const LIST_STYLES = {
  ul: 'list-style-type: disc; padding-left: 24px; margin: 0;',
  ol: 'list-style-type: decimal; padding-left: 24px; margin: 0;',
  li: 'margin: 0;',
};

function mergeStyle(existingStyle, defaults) {
  if (!existingStyle) return defaults;
  const existingProps = new Set(
    existingStyle
      .split(';')
      .map((s) => s.split(':')[0]?.trim().toLowerCase())
      .filter(Boolean)
  );
  const additions = defaults
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((decl) => {
      const prop = decl.split(':')[0]?.trim().toLowerCase();
      return prop && !existingProps.has(prop);
    })
    .join('; ');
  if (!additions) return existingStyle;
  return existingStyle.trim().replace(/;?$/, '; ') + additions + ';';
}

/**
 * Pass the editor's innerHTML; returns the same HTML with list elements
 * given inline bullet/numbering styles. No body wrapper, no font override,
 * no margin/line-height changes. WYSIWYG.
 */
export function inlineStylesForEmail(rawHtml) {
  if (!rawHtml) return '';
  if (typeof window === 'undefined' || !window.document) return rawHtml;

  const wrapper = window.document.createElement('div');
  wrapper.innerHTML = rawHtml;

  Object.keys(LIST_STYLES).forEach((tag) => {
    wrapper.querySelectorAll(tag).forEach((node) => {
      const existing = node.getAttribute('style');
      const merged = mergeStyle(existing, LIST_STYLES[tag]);
      if (merged) node.setAttribute('style', merged);
    });
  });

  return wrapper.innerHTML;
}

export default inlineStylesForEmail;
